import React, { useMemo, useState } from "react";
import { Search } from "lucide-react";

import { invalidateEventDefs, useEventDefs } from "@/api/useEventDefs";
import { RBACAccessLevel, RBACDomain } from "@/access-control/types";
import { Badge } from "@/design-system/components/Badge/Badge";
import { Banner } from "@/design-system/components/Banner/Banner";
import { Checkbox } from "@/design-system/components/Checkbox/Checkbox";
import { Input } from "@/design-system/components/Input/Input";
import { LoadingSpinner } from "@/design-system/components/LoadingSpinner/LoadingSpinner";
import { toast } from "@/design-system/components/Toast/Toast";
import { Typography } from "@/design-system/components/Typography/Typography";
import { useUserHasAccess } from "@/hooks/useUserHasAccess";
import { isDestinationToggleable } from "@/LibraryPageV2/utils";
import { makeRequest } from "@/util";
import { WizardLink } from "@/wizards/framework/WizardLink";
import { WizardStepLayout } from "@/wizards/framework/WizardStepLayout";
import { EnableDestinationStepProps } from "../stepProps";
import {
  isEventSelected,
  useEventsForDestination,
} from "../useEventsForDestination";

export function ChooseEventsStep({
  data,
  context,
  updateData,
  goNext,
}: EnableDestinationStepProps) {
  const { saasDestination: destination, configuredDestination } = context;
  const { data: eventDefs, isLoading } = useEventDefs();
  const enabledEvents = useEventsForDestination(data, context);
  const canWrite = useUserHasAccess(
    RBACDomain.eventLibrary,
    RBACAccessLevel.write,
  );
  const [search, setSearch] = useState("");
  const [pendingIds, setPendingIds] = useState<Set<string>>(new Set());

  const listedEvents = useMemo(
    () =>
      (eventDefs ?? []).filter(
        (event) => !event.is_hidden && !event.deleted_at,
      ),
    [eventDefs],
  );
  const visibleEvents = useMemo(() => {
    const term = search.trim().toLowerCase();
    return listedEvents.filter(
      (event) => !term || event.name.toLowerCase().includes(term),
    );
  }, [listedEvents, search]);
  const searchTerm = search.trim();

  async function toggle(eventDefId: string, state: boolean) {
    if (!configuredDestination) {
      return;
    }
    setPendingIds((ids) => new Set(ids).add(eventDefId));
    try {
      const response = await makeRequest<{ error_message?: string }>(
        "toggle_destination",
        {
          dest_id: configuredDestination.id,
          event_def_id: eventDefId,
          state,
        },
      );
      if (response.data.error_message) {
        toast.error(response.data.error_message);
        return;
      }
      updateData((latest) => ({
        eventToggles: { ...latest.eventToggles, [eventDefId]: state },
      }));
      await invalidateEventDefs();
    } catch (error) {
      toast.error("Couldn’t update the event. Try again.");
      console.error(error);
    } finally {
      setPendingIds((ids) => {
        const next = new Set(ids);
        next.delete(eventDefId);
        return next;
      });
    }
  }

  return (
    <WizardStepLayout
      title="Choose events to send"
      description={`Pick the events ${destination?.name ?? "this destination"} should receive.`}
      primaryAction={{
        label: "Continue",
        onClick: goNext,
        disabled: enabledEvents.length === 0,
        disabledReason: "Choose at least one event",
      }}
    >
      {!configuredDestination && (
        <Banner type="warning">Connect the destination first.</Banner>
      )}
      {canWrite === false && (
        <Banner type="warning">
          You don’t have permission to edit events. Ask an admin to choose the
          events {destination?.name ?? "this destination"} receives.
        </Banner>
      )}
      {listedEvents.length > 0 && (
        <Banner type="info" kind="note">
          Events sent with <code>freshpaint.track()</code>, including tags in
          Google Tag Manager, go to every destination automatically. Need a new
          event?{" "}
          <WizardLink
            to="/events/library"
            className="text-textActionSelected cursor-pointer underline"
          >
            Create it in the Event Library
          </WizardLink>
          , then come back.
        </Banner>
      )}
      <Input
        label="Search events"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        leadingIcon={<Search aria-hidden />}
      />
      {isLoading || !destination ? (
        <LoadingSpinner className="size-6" />
      ) : (
        <ul className="border-borderSubtle divide-borderSubtle max-h-96 divide-y overflow-auto rounded-lg border">
          {visibleEvents.map((event) => {
            const enabled =
              !!configuredDestination &&
              isEventSelected(event, configuredDestination, data);
            const toggleable = isDestinationToggleable(event, false);
            const id = `event-${event.id}`;
            return (
              <li key={event.id} className="flex items-center gap-3 px-4 py-3">
                <Checkbox
                  id={id}
                  checked={enabled}
                  disabled={
                    !toggleable ||
                    !configuredDestination ||
                    canWrite === false ||
                    pendingIds.has(event.id)
                  }
                  onCheckedChange={(checked) =>
                    void toggle(event.id, checked === true)
                  }
                />
                <label
                  htmlFor={id}
                  className="text-label-sm text-textPrimary flex-1 cursor-pointer truncate"
                >
                  {event.name}
                </label>
                {!toggleable && <Badge text="Sent automatically" />}
              </li>
            );
          })}
          {visibleEvents.length === 0 && (
            <li className="p-4">
              <Typography variant="body-sm" color="subtle">
                {searchTerm
                  ? `No events match “${searchTerm}”.`
                  : "No events yet."}
              </Typography>
            </li>
          )}
        </ul>
      )}
    </WizardStepLayout>
  );
}
